import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, FormGroup, Input, Label, Button } from 'reactstrap';
import { Calendar, ChevronDown, X } from 'react-feather';
import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import axios from 'axios';
import { baseURL } from '../../../../../Services/api/baseURL';

const CUSTOM_RANGE_PRESETS = [
    {
        label: 'Last 7 days',
        getRange: () => [moment().subtract(6, 'days').toDate(), moment().toDate()],
    },
    {
        label: 'Last month',
        getRange: () => [
            moment().subtract(1, 'month').startOf('month').toDate(),
            moment().subtract(1, 'month').endOf('month').toDate(),
        ],
    },
];

function FilterBar({
    filterPeriod,
    setFilterPeriod,
    filterStore,
    setFilterStore,
    filterCat,
    setFilterCat,
    filterSubcat,
    setFilterSubcat,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    stores = [],
    collections = [],
    subcollections = []
}) {
    const [showCustomDate, setShowCustomDate] = useState(false);
    const [draftRange, setDraftRange] = useState([startDate, endDate]);
    const [availableSubcollections, setAvailableSubcollections] = useState([]);
    const [loadingSubcollections, setLoadingSubcollections] = useState(false);
    const popoverRef = useRef(null);
    const triggerRef = useRef(null);

    // Close the custom range popover when clicking outside of it
    useEffect(() => {
        if (!showCustomDate) return;
        const handleClickOutside = (e) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                setShowCustomDate(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCustomDate]);

    // Fetch subcollections when collection is selected
    useEffect(() => {
        const fetchSubcollectionsByCollection = async () => {
            if (!filterCat) {
                setAvailableSubcollections([]);
                return;
            }
            
            try {
                setLoadingSubcollections(true);
                const token = JSON.parse(localStorage.getItem('token'));
                const response = await axios.get(
                    `${baseURL}/api/admin/get-sub-collections-by-collection/${filterCat}`,
                    { headers: { Authorization: `${token}` } }
                );
                setAvailableSubcollections(response?.data?.data || []);
            } catch (error) {
                console.error('Error fetching subcollections:', error);
                setAvailableSubcollections([]);
            } finally {
                setLoadingSubcollections(false);
            }
        };

        fetchSubcollectionsByCollection();
    }, [filterCat]);

    const handlePeriodClick = (period) => {
        setFilterPeriod(period);
        setShowCustomDate(false);
    };

    const handleCustomRangeToggle = () => {
        setDraftRange([startDate, endDate]);
        setShowCustomDate((prev) => !prev);
    };

    const handlePresetClick = (preset) => {
        setDraftRange(preset.getRange());
    };

    const handleClearCustomRange = () => {
        setDraftRange([null, null]);
    };

    const handleCancelCustomRange = () => {
        setDraftRange([startDate, endDate]);
        setShowCustomDate(false);
    };

    const handleApplyCustomRange = () => {
        const [draftStart, draftEnd] = draftRange;
        if (!draftStart || !draftEnd) return;
        setStartDate(draftStart);
        setEndDate(draftEnd);
        setFilterPeriod('custom');
        setShowCustomDate(false);
    };

    const handleCatChange = (e) => {
        const cat = e.target.value;
        setFilterCat(cat);
        setFilterSubcat(''); // Reset subcategory when category changes
    };

    const formatDateRange = () => {
        if (filterPeriod === 'today') {
            return moment().format('DD MMM YYYY');
        }
        if (filterPeriod === 'week') {
            const weekStart = moment().startOf('week');
            const weekEnd = moment().endOf('week');
            return `${weekStart.format('DD MMM')} – ${weekEnd.format('DD MMM YYYY')}`;
        }
        if (filterPeriod === 'month') {
            const monthStart = moment().startOf('month');
            const monthEnd = moment().endOf('month');
            return `${monthStart.format('DD MMM')} – ${monthEnd.format('DD MMM YYYY')}`;
        }
        if (filterPeriod === 'year') {
            const yearStart = moment().startOf('year');
            const yearEnd = moment().endOf('year');
            return `${yearStart.format('DD MMM')} – ${yearEnd.format('DD MMM YYYY')}`;
        }
        if (filterPeriod === 'custom' && startDate && endDate) {
            return `${moment(startDate).format('DD MMM')} – ${moment(endDate).format('DD MMM YYYY')}`;
        }
        return '';
    };

    return (
        <div className="gp-filter-section">
            {/* Period Buttons */}
            <div className="gp-period-bar">
                <div className="gp-period-buttons">
                    <Button 
                        className={`gp-period-btn ${filterPeriod === 'today' ? 'active' : ''}`}
                        onClick={() => handlePeriodClick('today')}
                        outline
                    >
                        Today
                    </Button>
                    <Button 
                        className={`gp-period-btn ${filterPeriod === 'week' ? 'active' : ''}`}
                        onClick={() => handlePeriodClick('week')}
                        outline
                    >
                        This week
                    </Button>
                    <Button 
                        className={`gp-period-btn ${filterPeriod === 'month' ? 'active' : ''}`}
                        onClick={() => handlePeriodClick('month')}
                        outline
                    >
                        This month
                    </Button>
                    <Button 
                        className={`gp-period-btn ${filterPeriod === 'year' ? 'active' : ''}`}
                        onClick={() => handlePeriodClick('year')}
                        outline
                    >
                        This year
                    </Button>
                    <div className="gp-custom-range-wrap" ref={triggerRef}>
                        <Button
                            className={`gp-period-btn ${filterPeriod === 'custom' ? 'active' : ''}`}
                            onClick={handleCustomRangeToggle}
                            outline
                        >
                            <Calendar size={14} className="me-1" />
                            Custom range
                            <ChevronDown size={14} className={`gp-chevron ${showCustomDate ? 'gp-chevron-open' : ''}`} />
                        </Button>

                        {showCustomDate && (
                            <div className="gp-custom-date-backdrop" onClick={handleCancelCustomRange} />
                        )}

                        {showCustomDate && (
                            <div className="gp-custom-date-popover" ref={popoverRef}>
                                <div className="gp-custom-date-presets">
                                    {CUSTOM_RANGE_PRESETS.map((preset) => (
                                        <button
                                            type="button"
                                            key={preset.label}
                                            className="gp-preset-btn"
                                            onClick={() => handlePresetClick(preset)}
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="gp-custom-date-calendar">
                                    <DatePicker
                                        selectsRange
                                        startDate={draftRange[0]}
                                        endDate={draftRange[1]}
                                        onChange={(update) => setDraftRange(update)}
                                        monthsShown={1}
                                        maxDate={new Date()}
                                        inline
                                    />
                                </div>

                                <div className="gp-custom-date-footer">
                                    <span className="gp-custom-date-selected">
                                        {draftRange[0] && draftRange[1]
                                            ? `${moment(draftRange[0]).format('DD MMM YYYY')} – ${moment(draftRange[1]).format('DD MMM YYYY')}`
                                            : 'Select a date range'}
                                    </span>
                                    <div className="gp-custom-date-actions">
                                        <button
                                            type="button"
                                            className="gp-date-action-btn gp-date-action-clear"
                                            onClick={handleClearCustomRange}
                                            disabled={!draftRange[0] && !draftRange[1]}
                                        >
                                            <X size={13} className="me-1" />
                                            Clear
                                        </button>
                                        <button
                                            type="button"
                                            className="gp-date-action-btn gp-date-action-cancel"
                                            onClick={handleCancelCustomRange}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="gp-date-action-btn gp-date-action-apply"
                                            onClick={handleApplyCustomRange}
                                            disabled={!draftRange[0] || !draftRange[1]}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <span className="gp-date-range">{formatDateRange()}</span>
            </div>

            {/* Filters */}
            <Row className="gp-filters-row">
                <Col lg={4} md={6} sm={12} className="mb-3">
                    <FormGroup>
                        <Label className="gp-filter-lbl">Store</Label>
                        <Input 
                            type="select"
                            className="gp-select"
                            value={filterStore}
                            onChange={(e) => setFilterStore(e.target.value)}
                        >
                            <option value="">All stores</option>
                            {stores.map(store => (
                                <option key={store._id || store.id} value={store._id || store.id}>
                                    {store.storeName}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col lg={4} md={6} sm={12} className="mb-3">
                    <FormGroup>
                        <Label className="gp-filter-lbl">Category</Label>
                        <Input 
                            type="select"
                            className="gp-select"
                            value={filterCat}
                            onChange={handleCatChange}
                        >
                            <option value="">All categories</option>
                            {collections.map(collection => (
                                <option key={collection._id || collection.id} value={collection._id || collection.id}>
                                    {collection.collection_name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col lg={4} md={6} sm={12} className="mb-3">
                    <FormGroup>
                        <Label className="gp-filter-lbl">Sub-category</Label>
                        <Input 
                            type="select"
                            className="gp-select"
                            value={filterSubcat}
                            onChange={(e) => setFilterSubcat(e.target.value)}
                            disabled={!filterCat || loadingSubcollections || availableSubcollections.length === 0}
                        >
                            <option value="">{loadingSubcollections ? 'Loading...' : 'All sub-categories'}</option>
                            {availableSubcollections.map(subCollection => (
                                <option key={subCollection._id || subCollection.id} value={subCollection._id || subCollection.id}>
                                    {subCollection.sub_collection_name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>
        </div>
    );
}

export default FilterBar;
